// Zustand store tracking in-flight uploads for the progress UI. One
// entry per ongoing upload keyed by a client-generated id; cleared when
// the upload resolves (success or failure).
//
// Each running upload also registers an AbortController so the whole
// batch can be cancelled when the session ends (logout / refresh
// failure): an upload must not keep streaming bytes — or try to
// finalise — on behalf of a user who is no longer signed in.

import { create } from "zustand";

import type { UploadStage } from "@/api/upload";

export interface UploadRecord {
  id: string;
  filename: string;
  size: number;
  stage: UploadStage;
  bytesDone: number;
  error: string | null;
  done: boolean;
}

interface UploadsState {
  byId: Record<string, UploadRecord>;
  add: (record: UploadRecord) => void;
  update: (id: string, patch: Partial<UploadRecord>) => void;
  remove: (id: string) => void;
  /** Abort every running upload and forget all records. */
  abortAll: () => void;
}

// Kept outside the reactive state: controllers aren't render data and
// swapping them must not re-render the progress panel.
const controllers = new Map<string, AbortController>();

/**
 * Create and register the AbortController for upload ``id``. The
 * caller must call {@link releaseUploadController} when the upload
 * settles.
 */
export function registerUploadController(id: string): AbortController {
  const controller = new AbortController();
  controllers.set(id, controller);
  return controller;
}

/** Drop the controller for a settled upload. */
export function releaseUploadController(id: string): void {
  controllers.delete(id);
}

export const useUploads = create<UploadsState>((set) => ({
  byId: {},
  add: (record) =>
    set((s) => ({ byId: { ...s.byId, [record.id]: record } })),
  update: (id, patch) =>
    set((s) => {
      const existing = s.byId[id];
      if (!existing) return s;
      return { byId: { ...s.byId, [id]: { ...existing, ...patch } } };
    }),
  remove: (id) =>
    set((s) => {
      const { [id]: _removed, ...rest } = s.byId;
      return { byId: rest };
    }),
  abortAll: () => {
    for (const controller of controllers.values()) {
      controller.abort();
    }
    controllers.clear();
    set({ byId: {} });
  },
}));
